/* eslint-disable react/exhaustive-deps */
import {Dropdown} from 'platform/components';
import {Icon} from 'platform/foundation';
import styled from 'styled-components';

import {Children, ReactNode, useEffect, useRef, useState} from 'react';

import {equals, isNil, sum} from 'ramda';

import {TestIdProps, useOnMount} from 'shared/web';

type MenuItemProps = {
  isActive?: boolean;
};

// `contain: inline-size` stops the items from sizing the menu: the top bar header cell is
// `flex: 1 0 auto`, so their natural width would stretch it under the top bar actions instead of
// moving the items that do not fit into the "more" dropdown. The menu therefore needs a parent that
// gives it a definite or flex-grown width; in a shrink-to-fit parent it would collapse to zero.
// Today that parent is `HeaderCell` in apps/dms TopBar.tsx, which grows only while `$canGrow` is
// true (global search is hidden on Sourcing). Showing global search here moves every item to "…".
// The minimum width keeps room for the "more" button (40 px), so the items stay reachable even when
// the title, the segment and the settings take the whole header.
const Container = styled.div`
  flex: 1 1 0;
  min-width: ${({theme}) => theme.getSize(10)};
  height: 100%;
  position: relative;
  contain: inline-size;
`;

// eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
const Ul = styled.ul`
  width: 100%;
  height: 100%;
  margin: 0;
  list-style-type: none;
  display: flex;
  flex-wrap: wrap;
  z-index: 1;

  .show-more {
    position: relative;

    & > ul {
      display: none;
    }

    &:hover {
      & > ul {
        display: block;
      }
    }
  }
`;

const CloneUl = styled(Ul)`
  position: absolute;
  z-index: -1;
  visibility: hidden;
`;

// eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
const Submenu = styled.ul`
  & > li {
    width: 100%;
    display: flex;
    padding: 6px 12px;
    margin-bottom: 4px;
    border-radius: ${({theme}) => theme.radii.small};

    &:before {
      display: none;
    }

    &:last-child {
      margin-bottom: 0;
    }

    &:hover {
      background-color: ${({theme}) => theme.colors.palettes.neutral[30][100]};

      &:before {
        display: none !important;
      }
    }
  }
`;

// eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
export const MenuItem = styled.li<MenuItemProps>`
  height: 100%;
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-right: 16px;
  flex: 0 1 auto;
  white-space: nowrap;
  cursor: pointer;

  &:last-child {
    margin-right: 0;
  }

  &:before {
    left: 0;
    bottom: 0;
    content: '';
    width: 100%;
    height: 4px;
    position: absolute;
    display: none;
  }

  &:not(.show-more):hover:before {
    display: block;
    background-color: ${({theme, isActive}) =>
      isActive ? theme.colors.palettes.blue[60][100] : theme.colors.palettes.neutral[30][100]};
  }

  ${({theme, isActive}) =>
    isActive
      ? `
				&:before {
					display: block;
					background-color: ${theme.colors.palettes.blue[60][100]};
				}
			`
      : null}
`;

// eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
const MoreButton = styled.button`
  width: 40px;
  height: 32px;
  border: 0;
  outline: 0;
  background-color: transparent;
  border-radius: ${({theme}) => theme.radii.small};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background-color: ${({theme}) => theme.colors.palettes.neutral[30][100]};
  }
`;

type MenuProps = TestIdProps & {
  className?: string;
  children?: ReactNode;
};

export const Menu = (props: MenuProps) => {
  const itemsWidth = useRef(0);
  const menuRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const moreBtn = useRef<HTMLLIElement>(null);
  const containerRef = useRef<HTMLUListElement>(null);
  const anchorEl = useRef<null | HTMLButtonElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [hiddenItems, setHiddenItems] = useState<number[]>([]);

  const childrenArray = Children.toArray(props.children);

  const onResize = () => {
    const moreButtonWidth = moreBtn.current?.offsetWidth ?? 0;
    const primaryWidth = containerRef.current?.offsetWidth ?? 0;
    const itemWidths = Array.from(
      containerRef.current?.querySelectorAll<HTMLLIElement>('li:not(.show-more)') ?? [],
      (item) => item.offsetWidth + parseInt(getComputedStyle(item).marginRight || '0', 10)
    );

    const nextHiddenItems: number[] = [];

    // The "more" button takes space only when something is hidden, so check first whether
    // everything fits without it.
    if (sum(itemWidths) > primaryWidth) {
      let usedWidth = 0;

      itemWidths.forEach((itemWidth, index) => {
        if (usedWidth + itemWidth <= primaryWidth - moreButtonWidth) {
          usedWidth += itemWidth;
        } else {
          nextHiddenItems.push(index);
        }
      });
    }

    setHiddenItems((previous) => (equals(previous, nextHiddenItems) ? previous : nextHiddenItems));
  };

  // The menu width also changes without a window resize, e.g. when the AI assistant panel opens or
  // the branch or availability select changes its label, so the overflow is recomputed whenever the
  // container itself resizes. The observer also watches the items of the measurement clone: they
  // can get wider while the container keeps its width (bold active tab, counters, fonts).
  // `hiddenItems` changes only the visible list, so neither target can loop back into `onResize`.
  useOnMount(
    function subscribeToResize() {
      const container = menuRef.current;

      if (isNil(container)) {
        return;
      }

      resizeObserverRef.current = new ResizeObserver(onResize);
      resizeObserverRef.current.observe(container);
      containerRef.current
        ?.querySelectorAll('li')
        .forEach((item) => resizeObserverRef.current?.observe(item));
    },
    function unsubscribeFromResize() {
      resizeObserverRef.current?.disconnect();
    }
  );

  useEffect(
    function measureItemsWidth() {
      if (containerRef.current) {
        containerRef.current?.childNodes?.forEach((node) => {
          const style = getComputedStyle(node as Element);
          const totalWidth =
            (node as HTMLLIElement).offsetWidth + parseInt(style.marginRight || '0', 10);

          return (itemsWidth.current += totalWidth);
        });

        onResize();
      }
    },
    [containerRef.current]
  );

  const handleMoreClick = () => {
    setIsOpen(!isOpen);
  };

  const moreButton = (
    <MoreButton aria-describedby="show-more-button" ref={anchorEl} onClick={handleMoreClick}>
      <Icon value="navigation/more_horiz" />
    </MoreButton>
  );

  return (
    // eslint-disable-next-line react/forbid-component-props -- Pre-existing violation
    <Container ref={menuRef} className={props.className} data-testid={props['data-testid']}>
      <CloneUl ref={containerRef}>
        {props.children}

        {/* eslint-disable-next-line react/forbid-component-props -- Pre-existing violation */}
        <MenuItem ref={moreBtn} className="show-more">
          {moreButton}
        </MenuItem>
      </CloneUl>

      <Ul>
        {hiddenItems.length
          ? childrenArray.map((item, i) => (hiddenItems.includes(i) ? null : item))
          : childrenArray}

        {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
        {hiddenItems.length > 0 ? (
          // eslint-disable-next-line react/forbid-component-props -- Pre-existing violation
          <MenuItem className="show-more">
            <Dropdown
              dropdownControl={
                <MoreButton>
                  <Icon value="navigation/more_horiz" />
                </MoreButton>
              }
            >
              <Submenu>
                {childrenArray.map((item, i) => (hiddenItems.includes(i) ? item : null))}
              </Submenu>
            </Dropdown>
          </MenuItem>
        ) : null}
      </Ul>
    </Container>
  );
};
